import { useCallback } from "react";
import { format } from "date-fns";
import * as orderService from "@/supabase/order/service/orderService";
import * as orderItemRepository from "@/supabase/order/repository/orderItemRepository";
import { Item, NewItem } from "@/types/purchase";
import { CustomerTemplate } from "@/components/CustomerTemplateManager";
import { logError } from "@/lib/sentry";

// const MAX_RECENT_ITEMS = 12; // No longer needed, catalog has no limit

/**
 * 고객 ID가 주문 ID인 경우 숫자 ID를 추출하는 함수
 */
const extractOrderId = (customerId: string | null): number | null => {
  if (customerId && customerId.startsWith('order_')) {
    return Number(customerId.replace('order_', ''));
  }
  return null;
};

type CustomerTypes = 'temp' | 'default' | 'order' | 'none';

/**
 * 고객 ID 유형을 결정하는 함수
 */
const getCustomerType = (
  customerId: string | null | undefined, 
  activeCustomer: CustomerTemplate | null
): CustomerTypes => {
  if (!customerId || !activeCustomer) return 'none';
  
  if (customerId.startsWith('temp_')) return 'temp';
  if (customerId.startsWith('default_')) return 'default';
  if (customerId.startsWith('order_')) return 'order';
  
  return 'none';
};

interface UseCustomerItemOperationsProps {
  user: any;
  pseudoUserId: string;
  activeCustomerId: string | null;
  activeCustomer: CustomerTemplate | null;
  currentItems: Item[];
  setCurrentItems: (items: Item[]) => void;
  updateDateOrderTemplates: (updater: CustomerTemplate[] | ((prev: CustomerTemplate[]) => CustomerTemplate[])) => void;
  selectedDate: Date;
  originalAddItem: (payload: NewItem) => Promise<void>;  // usePurchaseState.addItem
  originalAddRecentItem: (item: Item) => void;
  setActiveCustomerId: (id: string | null) => void;
  // recentItems: Item[]; // Removed
  // setRecentItems: (items: Item[]) => void; // Removed
  addOrUpdateItemInCatalog: (itemToAdd: Item) => void; // Added
  // 새로 생성한 order_ id를 "서버 미반영 보존 대상"으로 Home에 등록 (stale 조회로 인한 삭제 방지)
  markOrderIdPending?: (orderId: string) => void;
  // 기존 주문 가격 뮤테이션 성공 후 status reconcile — 자동 전환 반영 (스펙 §16.10)
  onOrderMutated?: (orderId: number) => void;
}

/**
 * 고객 타입별 상품 추가 로직을 관리하는 커스텀 훅
 */
export function useCustomerItemOperations({
  user,
  // pseudoUserId, // Removed as it's unused in this hook after recent changes
  activeCustomerId,
  activeCustomer,
  currentItems,
  setCurrentItems,
  updateDateOrderTemplates,
  selectedDate,
  originalAddItem,
  originalAddRecentItem,
  setActiveCustomerId,
  // recentItems, // Removed
  // setRecentItems // Removed
  addOrUpdateItemInCatalog, // Added
  markOrderIdPending,
  onOrderMutated
}: UseCustomerItemOperationsProps) {
  // /**
  //  * 최근 추가한 상품 목록을 업데이트하는 함수 - This logic is now in useProductCatalog
  //  */
  // const updateRecentItems = useCallback((newItemData: Item) => {
  //   // 항상 sessionStorage에서 최신 상태 가져오기
  //   const userId = user?.id || pseudoUserId;
  //   const savedItems = sessionStorage.getItem(`recentItems_${userId}`);
  //   let currentRecentItems = recentItems;
    
  //   if (savedItems) {
  //     try {
  //       currentRecentItems = JSON.parse(savedItems);
  //     } catch (e) {
  //       // 저장된 아이템 파싱 실패 시 현재 상태 유지
  //       console.error("저장된 최근 아이템 파싱 실패:", e);
  //     }
  //   }
    
  //   // 최근 상품 목록 업데이트 - 같은 상품명이면 가격 업데이트
  //   const existingRecentIndex = currentRecentItems.findIndex(item =>
  //     item.name === newItemData.name
  //   );

  //   if (existingRecentIndex !== -1) {
  //     // 같은 상품명이 있으면 해당 항목 제거 후 새로운 가격으로 맨 앞에 추가
  //     const updatedRecentItems = currentRecentItems.filter((_, index) => index !== existingRecentIndex);
  //     const newRecentItems = [{ ...newItemData, quantity: 1 }, ...updatedRecentItems].slice(0, MAX_RECENT_ITEMS);
      
  //     // 상태 및 세션 스토리지 업데이트
  //     setRecentItems(newRecentItems);
  //     sessionStorage.setItem(`recentItems_${userId}`, JSON.stringify(newRecentItems));
  //   } else {
  //     // 새로운 상품이면 맨 앞에 추가
  //     const newRecentItems = [{ ...newItemData, quantity: 1 }, ...currentRecentItems].slice(0, MAX_RECENT_ITEMS);
      
  //     // 상태 및 세션 스토리지 업데이트
  //     setRecentItems(newRecentItems);
  //     sessionStorage.setItem(`recentItems_${userId}`, JSON.stringify(newRecentItems));
  //   }
  // }, [user, pseudoUserId, recentItems, setRecentItems]);

  // (updateLocalCustomerCache 제거 — A2: setCurrentItems(=chokepoint)가 source를 단일 갱신.
  //  stale dateOrderTemplates 클로저로 배열을 통째 교체하던 로직은 무관 필드 clobber 위험이 있어 삭제.)

  // 고객 교체 헬퍼 함수
  // 함수형 업데이터 필수: createWithItem await 동안 templates가 바뀔 수 있어
  // 클로저 스냅샷으로 통째 교체하면 **다른 행**의 동시 갱신을 덮어쓴다(활성 고객 누락 → 선택 점프).
  // 단, 교체되는 행 자신은 pre-await 스냅샷(newCustomer)이 이긴다(stale-write-wins) —
  // 구 코드와 동일한 의미론을 의도적으로 유지(DB에 저장된 값과의 정합 우선).
  const replaceCustomerInTemplates = useCallback((oldCustomerId: string | null, newCustomer: CustomerTemplate) => {
    if (!oldCustomerId) return;

    updateDateOrderTemplates(prev => {
      // await 중 refreshOrders가 같은 주문(newCustomer.id)을 먼저 반영했을 수 있음 —
      // 제거하지 않으면 id 중복(React key 충돌·find 모호성).
      const base = prev.filter(c => c.id !== newCustomer.id);
      const customerIndex = base.findIndex(c => c.id === oldCustomerId);

      if (customerIndex !== -1) {
        const updatedTemplates = [...base];
        updatedTemplates.splice(customerIndex, 1, newCustomer);
        return updatedTemplates;
      }
      return [...base, newCustomer];
    });
  }, [updateDateOrderTemplates]);

  // 새 상품 추가 로직 (성공 시 true, 실패 시 false 반환)
  const addItem = useCallback(async (payload: NewItem): Promise<boolean> => {
    // 상품명과 가격 유효성 검사
    if (!payload.name.trim()) {
      alert("상품명을 입력해주세요.");
      return false;
    }

    // 가격 입력이 비어있으면(placeholder만 보이는 상태 등) 0원으로 간주
    const rawPrice = payload.price.trim();
    const price = rawPrice === "" ? 0 : Number(rawPrice);
    if (isNaN(price)) {
      alert("올바른 가격을 입력해주세요.");
      return false;
    }

    if (price < 0 && !confirm("상품 가격이 음수입니다. 정말 추가하시겠습니까?")) {
      return false;
    }

    const newItemData = {
      name: payload.name.trim(),
      price: price,
      quantity: Number(payload.quantity)
    };

    // 같은 이름과 가격의 상품이 있는지 확인
    const existingItemIndex = currentItems.findIndex(
      item => item.name === newItemData.name && item.price === newItemData.price
    );

    // 로그인한 사용자이고 활성 고객이 있는 경우에만 특별 처리
    if (user && activeCustomerId) {
      // 로컬 UI 먼저 업데이트 (불변 복사 — currentItems===source.items이므로 in-place 변형 금지)
      let updatedItemsForUI: Item[];
      if (existingItemIndex !== -1) {
        // 이미 존재하는 상품이면 수량만 증가
        updatedItemsForUI = currentItems.map((it, i) =>
          i === existingItemIndex ? { ...it, quantity: it.quantity + newItemData.quantity } : it
        );
      } else {
        // 새로운 상품이면 추가
        updatedItemsForUI = [...currentItems, newItemData];
      }
      // setCurrentItems(=chokepoint)가 source(items+totalPrice)를 단일 갱신.
      setCurrentItems(updatedItemsForUI);

      // 상품 카탈로그에 아이템 추가/업데이트
      addOrUpdateItemInCatalog(newItemData);

      const customerType = getCustomerType(activeCustomerId, activeCustomer);
      
      switch (customerType) {
        case 'temp':
          await handleAddItemForTempCustomer(newItemData, updatedItemsForUI);
          return true;
        case 'default':
          await handleAddItemForDefaultCustomer(newItemData, updatedItemsForUI);
          return true;
        case 'order':
          await handleAddItemForOrderCustomer(newItemData, existingItemIndex, updatedItemsForUI);
          return true;
      }
      return true;
    }

    // 기본 로직 (비로그인 사용자 또는 특별 케이스가 아닌 경우)
    await originalAddItem(payload); // This will call addOrUpdateItemInCatalog internally via usePurchaseState
    return true;
  }, [
    user,
    activeCustomerId,
    activeCustomer,
    originalAddItem,
    updateDateOrderTemplates,
    selectedDate,
    setActiveCustomerId,
    currentItems,
    setCurrentItems,
    addOrUpdateItemInCatalog,
    getCustomerType
  ]);

  // 최근 상품 추가 로직
  const addRecentItem = useCallback(async (item: Item) => {
    // 상품 데이터 유효성 검사
    if (!item || !item.name.trim()) {
      alert("상품명이 없는 항목은 추가할 수 없습니다.");
      return;
    }
    
    if (isNaN(item.price)) {
      alert("올바른 가격 정보가 없는 항목은 추가할 수 없습니다.");
      return;
    }
    
    // 수량이 유효하지 않으면 기본값 1로 설정
    const validatedItem = {
      ...item,
      quantity: isNaN(item.quantity) || item.quantity <= 0 ? 1 : item.quantity
    };

    // 같은 이름과 가격의 상품이 있는지 확인
    const existingItemIndex = currentItems.findIndex(
      existing => existing.name === validatedItem.name && existing.price === validatedItem.price
    );

    // 로그인한 사용자이고 활성 고객이 있는 경우에만 특별 처리
    if (user && activeCustomerId) {
      // 로컬 UI 먼저 업데이트 (불변 복사 — currentItems===source.items이므로 in-place 변형 금지)
      let updatedItemsForUI: Item[];
      if (existingItemIndex !== -1) {
        // 이미 존재하는 상품이면 수량만 증가
        updatedItemsForUI = currentItems.map((it, i) =>
          i === existingItemIndex ? { ...it, quantity: it.quantity + validatedItem.quantity } : it
        );
      } else {
        // 새로운 상품이면 추가
        updatedItemsForUI = [...currentItems, validatedItem];
      }
      // setCurrentItems(=chokepoint)가 source(items+totalPrice)를 단일 갱신.
      setCurrentItems(updatedItemsForUI);

      const customerType = getCustomerType(activeCustomerId, activeCustomer);
      
      switch (customerType) {
        case 'temp':
          await handleRecentItemForTempCustomer(validatedItem, existingItemIndex, updatedItemsForUI);
          return;
        case 'default':
          await handleRecentItemForDefaultCustomer(validatedItem, existingItemIndex, updatedItemsForUI);
          return;
        case 'order':
          await handleRecentItemForOrderCustomer(validatedItem, existingItemIndex, updatedItemsForUI);
          return;
      }
      return;
    }

    // 기본 로직 (비로그인 사용자 또는 특별 케이스가 아닌 경우)
    originalAddRecentItem(validatedItem);
  }, [
    user, 
    activeCustomerId, 
    activeCustomer, 
    originalAddRecentItem,
    updateDateOrderTemplates,
    selectedDate,
    setActiveCustomerId,
    currentItems,
    setCurrentItems,
    getCustomerType
  ]);

  // 임시 고객에 대한 상품 추가 처리
  const handleAddItemForTempCustomer = async (itemData: Item, currentUIItems: Item[]) => {
    try {
      if (!activeCustomer) return;
      
      // 백그라운드에서 DB 작업 수행
      const orderId = await orderService.createWithItem(
        user.id,
        activeCustomer.nickname,
        format(selectedDate, "yyyy-MM-dd"),
        {
          itemName: itemData.name,
          price: itemData.price,
          quantity: itemData.quantity
        }
      );
      const newCustomerId = `order_${orderId}`;
      markOrderIdPending?.(newCustomerId);

      // 임시 고객을 실제 주문 고객으로 교체
      const totalPrice = currentUIItems.reduce((sum, item) => sum + (item.price * item.quantity), 0);
      replaceCustomerInTemplates(activeCustomerId, {
        id: newCustomerId,
        nickname: activeCustomer.nickname,
        template: activeCustomer.template,
        items: currentUIItems,
        is_keep: false,
        status: "미지정",
        memo: activeCustomer.memo || "",
        totalPrice,
        createdAt: new Date().toISOString(),
        paymentMethod: activeCustomer.paymentMethod ?? null,
        order_date: format(selectedDate, "yyyy-MM-dd")
      });

      setActiveCustomerId(newCustomerId);
      // console.log(`Replaced temp customer ${activeCustomerId} with order customer ${newCustomerId}`);
    } catch (error) {
      logError("주문 생성 실패:", error);
      alert("주문 생성에 실패했습니다.");
    }
  };

  // 기본 고객에 대한 상품 추가 처리
  const handleAddItemForDefaultCustomer = async (itemData: Item, currentUIItems: Item[]) => {
    try {
      if (!activeCustomer) return;

      // 백그라운드에서 DB 작업 수행
      const orderId = await orderService.createWithItem(
        user.id,
        activeCustomer.nickname,
        format(selectedDate, "yyyy-MM-dd"),
        {
          itemName: itemData.name,
          price: itemData.price,
          quantity: itemData.quantity
        }
      );
      const newCustomerId = `order_${orderId}`;
      markOrderIdPending?.(newCustomerId);

      // 기본 고객을 실제 주문 고객으로 교체
      const totalPrice = currentUIItems.reduce((sum, item) => sum + (item.price * item.quantity), 0);
      replaceCustomerInTemplates(activeCustomerId, {
        id: newCustomerId,
        nickname: activeCustomer.nickname,
        template: activeCustomer.template,
        items: currentUIItems,
        is_keep: false,
        status: "미지정",
        memo: activeCustomer.memo || "",
        totalPrice,
        createdAt: new Date().toISOString(),
        paymentMethod: activeCustomer.paymentMethod ?? null,
        order_date: format(selectedDate, "yyyy-MM-dd")
      });

      setActiveCustomerId(newCustomerId);
      // console.log(`Replaced default customer ${activeCustomerId} with order customer ${newCustomerId}`);
    } catch (error) {
      logError("주문 생성 실패:", error);
      alert("주문 생성에 실패했습니다.");
    }
  };

  // 주문 고객에 대한 상품 추가 처리
  const handleAddItemForOrderCustomer = async (itemData: Item, existingItemIndex: number, currentUIItems: Item[]) => {
    try {
      const orderId = extractOrderId(activeCustomerId);
      if (!orderId) return;

      // 상품이 없는 'order_' 고객 (방금 생성됨) 또는 상품이 있는 기존 고객 모두 addItem을 사용
      if (existingItemIndex !== -1) {
        // 증가된 총 수량은 갱신된 UI 배열(currentUIItems)에 있음.
        // (currentItems는 불변화 이후 옛 수량 → 사용 금지.)
        const existingItemInCart = currentUIItems[existingItemIndex];
        
        try {
          const orderItems = await orderItemRepository.findByOrderId(orderId);
          if (!orderItems || orderItems.length === 0) {
            throw new Error("주문 항목이 없습니다.");
          }
          
          const dbOrderItem = orderItems.find(dbItem => 
            dbItem.item_name === itemData.name && dbItem.price === itemData.price
          );
          
          if (dbOrderItem) {
            // DB에 아이템이 이미 존재: 수량 업데이트
            // currentItems[existingItemIndex].quantity 는 addItem에서 이미 증가된 총 수량
            await orderService.updateItem(orderId, dbOrderItem.id, { quantity: existingItemInCart.quantity });
            // console.log(`Updated quantity for item ${dbOrderItem.id} in order ${orderId} to ${existingItemInCart.quantity}`);
          } else {
            // DB에 아이템 없음: 새로 추가 (이 경우는 드물지만, 방어적으로)
            await orderService.addItem(orderId, {
              itemName: itemData.name,
              price: itemData.price,
              quantity: itemData.quantity // 이 상품의 초기 추가 수량
            });
            // console.log(`Added new item to order ${orderId} (fallback for existing order, item not found in DB)`);
          }
          onOrderMutated?.(orderId);   // 기존 주문 total_price 변경 커밋 후 status reconcile (§16.10)
        } catch (error) {
          // console.error("수량 업데이트/신규 아이템 추가(order_customer) 실패:", error);
          // 실패 시 UI는 이미 낙관적으로 업데이트 되었으므로, 롤백 또는 사용자 알림 필요
          alert("상품 수량 업데이트에 실패했습니다.");
        }
      } else {
        // 새 상품 추가 (UI는 addItem에서 이미 업데이트됨)
        await orderService.addItem(orderId, {
          itemName: itemData.name,
          price: itemData.price,
          quantity: itemData.quantity
        });
        onOrderMutated?.(orderId);   // 기존 주문 total_price 변경 커밋 후 status reconcile (§16.10)
      }
    } catch (error) {
      logError("상품 추가 실패(order_customer):", error);
      alert("상품 추가에 실패했습니다.");
    }
  };

  // 임시 고객에 대한 최근 상품 추가 처리
  const handleRecentItemForTempCustomer = async (item: Item, _existingItemIndex: number, currentUIItems: Item[]) => {
    try {
      if (!activeCustomer) return;
      
      // 백그라운드에서 새 상품과 함께 주문 생성
      const orderId = await orderService.createWithItem(
        user.id,
        activeCustomer.nickname,
        format(selectedDate, "yyyy-MM-dd"),
        {
          itemName: item.name, // item은 최근 상품 목록에서 클릭한 아이템
          price: item.price,
          // currentUIItems에는 이미 이 아이템이 포함되어 있고, 수량도 업데이트 되었을 수 있음.
          // addRecentItem에서 validatedItem.quantity (보통 1)로 추가했으므로, 그 수량을 사용.
          quantity: item.quantity 
        }
      );
      
      const newCustomerId = `order_${orderId}`;
      markOrderIdPending?.(newCustomerId);
      
      const totalPrice = currentUIItems.reduce((sum, item) => sum + (item.price * item.quantity), 0);
      replaceCustomerInTemplates(activeCustomerId, {
        id: newCustomerId,
        nickname: activeCustomer.nickname,
        template: activeCustomer.template,
        items: currentUIItems, // UI에 반영된 최신 아이템 목록 사용
        is_keep: false,
        status: "미지정",
        memo: activeCustomer.memo || "",
        totalPrice,
        createdAt: new Date().toISOString(),
        paymentMethod: activeCustomer.paymentMethod ?? null,
        order_date: format(selectedDate, "yyyy-MM-dd")
      });

      setActiveCustomerId(newCustomerId);
      // updateRecentItems(item); // Catalog update is handled by originalAddRecentItem or directly
      addOrUpdateItemInCatalog(item); // Ensure catalog is updated
      // console.log("Created order with recent item, ID:", orderId);
    } catch (error) {
      logError("주문 생성 실패(recent_temp):", error);
      alert("주문 생성에 실패했습니다.");
    }
  };

  // 기본 고객에 대한 최근 상품 추가 처리
  const handleRecentItemForDefaultCustomer = async (item: Item, _existingItemIndex: number, currentUIItems: Item[]) => {
    try {
      if (!activeCustomer) return;
      
      const orderId = await orderService.createWithItem(
        user.id,
        activeCustomer.nickname,
        format(selectedDate, "yyyy-MM-dd"),
        {
          itemName: item.name,
          price: item.price,
          quantity: item.quantity // addRecentItem에서 validated된 quantity 사용
        }
      );
      
      const newCustomerId = `order_${orderId}`;
      markOrderIdPending?.(newCustomerId);
      
      const totalPrice = currentUIItems.reduce((sum, item) => sum + (item.price * item.quantity), 0);
      replaceCustomerInTemplates(activeCustomerId, {
        id: newCustomerId,
        nickname: activeCustomer.nickname,
        template: activeCustomer.template,
        items: currentUIItems,
        is_keep: false,
        status: "미지정",
        memo: activeCustomer.memo || "",
        totalPrice,
        createdAt: new Date().toISOString(),
        paymentMethod: activeCustomer.paymentMethod ?? null,
        order_date: format(selectedDate, "yyyy-MM-dd")
      });

      setActiveCustomerId(newCustomerId);
      // updateRecentItems(item);
      addOrUpdateItemInCatalog(item);
      // console.log("Created order with recent item, ID:", orderId, "and replaced default customer");
    } catch (error) {
      logError("주문 생성 실패(recent_default):", error);
      alert("주문 생성에 실패했습니다.");
    }
  };

  // 주문 고객에 대한 최근 상품 추가 처리
  const handleRecentItemForOrderCustomer = async (item: Item, existingItemIndex: number, currentUIItems: Item[]) => {
    try {
      const orderId = extractOrderId(activeCustomerId);
      if (!orderId) return;

      // 상품이 없는 'order_' 고객 (방금 생성됨) 또는 상품이 있는 기존 고객 모두 addItem을 사용
      if (existingItemIndex !== -1) {
        // 카트에 이미 아이템이 있었음 (수량 증가됨) — 갱신된 UI 배열에서 최신 수량을 읽음.
        const existingItemInCart = currentUIItems[existingItemIndex];
        
        try {
          const orderItems = await orderItemRepository.findByOrderId(orderId);
          if (!orderItems || orderItems.length === 0) throw new Error("주문 항목이 없습니다.");
          
          const dbOrderItem = orderItems.find(dbItem =>
            dbItem.item_name === item.name && dbItem.price === item.price
          );
          
          if (dbOrderItem) {
            await orderService.updateItem(orderId, dbOrderItem.id, { quantity: existingItemInCart.quantity });
            // console.log(`Updated quantity for item ${dbOrderItem.id} in order ${orderId} to ${existingItemInCart.quantity} (recent_order)`);
          } else {
            await orderService.addItem(orderId, {
              itemName: item.name,
              price: item.price,
              quantity: item.quantity // 새 아이템으로 추가 시 validated quantity 사용
            });
            // console.log(`Added new item to order ${orderId} (fallback for recent_order, item not found in DB)`);
          }
          onOrderMutated?.(orderId);   // 기존 주문 total_price 변경 커밋 후 status reconcile (§16.10)
        } catch (error) {
          // console.error("수량 업데이트/신규 아이템 추가(recent_order_customer) 실패:", error);
          alert("상품 수량 업데이트에 실패했습니다.");
        }
      } else {
        // 카트에 없던 새 아이템 (UI에는 이미 추가됨)
        await orderService.addItem(orderId, {
          itemName: item.name,
          price: item.price,
          quantity: item.quantity // validated quantity from addRecentItem
        });
        onOrderMutated?.(orderId);   // 기존 주문 total_price 변경 커밋 후 status reconcile (§16.10)
      }
      
      // updateRecentItems(item); // Catalog update is handled by originalAddRecentItem or directly
      addOrUpdateItemInCatalog(item);
    } catch (error) {
      logError("상품 추가 실패(recent_order_customer):", error);
      alert("상품 추가에 실패했습니다.");
    }
  };

  return {
    addItem,
    addRecentItem
  };
}